import { getUnixTime } from "date-fns";
import type { ISlot } from "AppDB";
import { SLOT_TYPES } from "../types/SlotTypes";

import debug from "debug";
const log = debug("@ikew:ManageService");

export class ManageService {
	db: any = null;
	hub: string;
	store: string;

	constructor(db: any, storeId: string) {
		this.db = db;
		this.store = storeId;
		this.hub = import.meta.env.VITE_HUB_URI || "";
	}

	activeSlots(): Promise<ISlot[]> {
		const { db, store } = this;
		return new Promise((resolve, reject) => {
			const slots: ISlot[] = [];
			db.collection(store)
				.where("state", "!=", SLOT_TYPES.CLEARED)
				.get()
				.then((snapshot: any) => {
					snapshot.forEach((doc: any) => {
						slots.push({
							id: doc.id,
							...doc.data(),
						});
					});
					resolve(slots);
				})
				.catch((error: Error) => {
					reject(error);
				});
		});
	}

	addInProgress(slot: IAddInProgress): Promise<ISlot> {
		return new Promise((resolve, reject) => {
			const { db, store } = this;
			const inProgress = {
				...slot,
				state: SLOT_TYPES.IN_PROGRESS,
				created: getUnixTime(new Date()),
			};
			db.collection(store)
				.add(inProgress)
				.then(async (docRef: any) => {
					const persistedSlot = await docRef.get("cache");
					resolve({
						id: persistedSlot.id,
						...persistedSlot.data(),
					});
				})
				.catch((err: Error) => {
					reject(err);
				});
		});
	}

	async inProgressToReady(slot: ISlot): Promise<boolean> {
		const { db, store } = this;
		const now = getUnixTime(new Date());
		const ref = db.collection(store).doc(slot.id);
		await ref.update({
			state: 2,
			readyDate: now,
		});
		return true;
	}

	async clear(slot: ISlot): Promise<boolean> {
		const { db, store } = this;
		const ref = db.collection(store).doc(slot.id);
		await ref.delete();
		return true;
	}

	async clearReady(): Promise<boolean> {
		const { db, store } = this;
		const batch = db.batch();
		try {
			const snapshot = await db
				.collection(store)
				.where("state", "==", 2)
				.get();
			if (snapshot.length === 0) {
				return true;
			}
			snapshot.forEach((doc: any) => {
				batch.delete(doc.ref);
			});
			await batch.commit();
			return true;
		} catch (err) {
			log(err);
			throw err;
		}
	}

	async removePreparing(): Promise<boolean> {
		const { db, store } = this;
		const batch = db.batch();
		const snapshot = await db
			.collection(store)
			.where("state", "==", 1)
			.get();
		if (snapshot.length === 0) {
			return true;
		}
		snapshot.forEach((doc: any) => {
			batch.delete(doc.ref);
		});
		await batch.commit();
		return true;
	}

	async bumpReady(slot: ISlot): Promise<boolean> {
		const { db, store } = this;
		const now = getUnixTime(new Date());
		const ref = db.collection(store).doc(slot.id);
		await ref.update({
			readyDate: now,
		});
		return true;
	}

	async setWaitTime(waitTime: number, expire: number): Promise<boolean> {
		const { db, store } = this;
		const ref = db.collection(store).doc("_state");
		await ref.set(
			{
				waitTime,
				expire,
			},
			{ merge: true },
		);
		return true;
	}

	async resetWaitTime(): Promise<boolean> {
		const { db, store } = this;
		const ref = db.collection(store).doc("_state");
		await ref.set(
			{
				waitTime: null,
				expire: null,
			},
			{ merge: true },
		);
		return true;
	}
}

interface IAddInProgress {
	group: string;
	label: string;
}
